import React from "react";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { PageHeader } from "../components/PageHeader";
import { StatsRibbon } from "../components/StatsRibbon";
import { Testimonials } from "../components/Testimonials";
import { CtaBanner } from "../components/CtaBanner";
import { Footer } from "../components/Footer";
import { useLang } from "../i18n/LanguageContext";
import { productImages } from "../data/images";

const VALUES = {
  de: [
    {
      title: "Ehrliches Aufmaß",
      body: "Wir messen jeden Raum millimetergenau und kalkulieren ohne versteckte Quadratmeter.",
    },
    {
      title: "Eigenes Verlegeteam",
      body: "Keine Subunternehmer. Unsere Meister kennen jedes Material aus der Werkstatt.",
    },
    {
      title: "Lokal verwurzelt",
      body: "Mülheim, Duisburg, Essen, Oberhausen — wir kennen die Häuser, weil wir hier leben.",
    },
    {
      title: "Barrierefrei",
      body: "Rollstuhlgerechter Eingang, Parkplatz und Sitzgelegenheit im Showroom.",
    },
  ],
  en: [
    {
      title: "Honest measuring",
      body: "We measure every room to the millimetre and calculate with no hidden square metres.",
    },
    {
      title: "In-house installers",
      body: "No sub-contractors. Our master craftsmen know every material from the workshop floor.",
    },
    {
      title: "Locally rooted",
      body: "Mülheim, Duisburg, Essen, Oberhausen — we know the buildings because we live here.",
    },
    {
      title: "Accessible",
      body: "Wheelchair-friendly entrance, parking and seating throughout the showroom.",
    },
  ],
};

export default function About() {
  const { lang, t } = useLang();
  const values = VALUES[lang] || VALUES.de;
  return (
    <main className="bg-[#F9F8F6] text-[#1A1A1A] font-body" data-testid="page-about">
      <Navbar transparentOnHome={false} />
      <PageHeader
        eyebrow={lang === "de" ? "Über uns" : "About us"}
        title={
          lang === "de"
            ? "Eine kleine Werkstatt, die nie aufgehört hat zuzuhören."
            : "A small workshop that never stopped listening."
        }
        subtitle={
          lang === "de"
            ? "Seit 1985 verlegt Pick Bodenbeläge Böden im Ruhrgebiet. Wir sind Familie, Handwerk und Nachbar — in dieser Reihenfolge."
            : "Since 1985 Pick Bodenbeläge has been installing floors across the Ruhr area. We are family, craft and neighbour — in that order."
        }
        image={productImages.carpet}
      />

      <StatsRibbon />

      <section className="py-24 lg:py-32 bg-[#F9F8F6]" data-testid="about-story">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A5A44] mb-5">
              — {lang === "de" ? "Unsere Geschichte" : "Our story"}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95]">
              {lang === "de"
                ? "Vom Familientisch in den Showroom."
                : "From the family table to the showroom."}
            </h2>
          </div>
          <div className="lg:col-span-7 text-[#4A4A4A] text-base lg:text-lg leading-relaxed space-y-6 max-w-2xl">
            <p>
              {lang === "de"
                ? "Hubert Pick eröffnete 1985 die erste kleine Werkstatt in der Mülheimer Innenstadt — mit einem Renault-Lieferwagen, einer Säge und der festen Überzeugung, dass ein Boden nicht verkauft, sondern empfohlen werden muss."
                : "Hubert Pick opened the first small workshop in Mülheim in 1985 — with a Renault delivery van, a saw and the firm belief that a floor should never be sold, only recommended."}
            </p>
            <p>
              {lang === "de"
                ? "Heute, vier Jahrzehnte später, führt die zweite Generation den Betrieb mit derselben Haltung: persönlich beraten, ehrlich kalkulieren, sauber verlegen. Aus dem Lieferwagen wurden drei. Aus der Werkstatt ein Showroom an der Schultenhofstraße 46."
                : "Today, four decades later, the second generation runs the company with the same attitude: advise personally, calculate honestly, install cleanly. The one delivery van became three. The workshop became a showroom at Schultenhofstraße 46."}
            </p>
            <p>
              {lang === "de"
                ? "Geblieben sind 220 Google-Bewertungen, 4,5 Sterne — und das Versprechen, dass ein Anruf nach der Verlegung kein Aufwand ist, sondern eine Selbstverständlichkeit."
                : "What has remained: 220 Google reviews, 4.5 stars — and the promise that a call after the installation is never a chore, but a given."}
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#EFEBE6]" data-testid="about-values">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#8A5A44] mb-5">
            — {lang === "de" ? "Werte" : "Values"}
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95] mb-14 max-w-3xl">
            {lang === "de" ? "Vier Versprechen, die nie verhandelt werden." : "Four promises we never negotiate."}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {values.map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="bg-[#F9F8F6] hairline p-8 lg:p-10"
                data-testid={`about-value-${i}`}
              >
                <div className="tabular text-xs tracking-[0.2em] uppercase text-[#8A5A44] mb-4">0{i + 1}</div>
                <h3 className="font-display text-2xl lg:text-3xl font-light mb-3">{v.title}</h3>
                <p className="text-[#4A4A4A] leading-relaxed">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <CtaBanner variant="dark" />
      <Footer />
    </main>
  );
}
